/**
 * Wartezeiten im Portal — reiner Lesezustand.
 *
 * Berechnet werden die Wartezeiten ausschliesslich serverseitig durch die Cloud
 * Functions (nextcab_mobile/functions/waitTimeCalculator.js). Das Portal hoerte
 * frueher selbst nach: Jedes offene Fenster rechnete mit, schrieb nach
 * `waitTimes/current` und verdraengte dabei die Cloud Function — ein
 * Unternehmer-Portal konnte so globale Wartezeiten aus seinem gefilterten
 * Datenbestand schreiben.
 *
 * Konzept: docs/KONZEPT_WARTEZEIT_RUECKBAU_INS_BACKEND.md
 */
import { createSlice } from '@reduxjs/toolkit';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';

const initialState = {
  waitTimes: {
    general: "Berechnung...",
    grossraum: "Berechnung...",
    kleinbus7: "Berechnung...",
    kleinbus8: "Berechnung..."
  },
  nextUnavailable: null,
  metadata: null,
  isManagerActive: false,
  lastCalculationTime: null,
  isLoading: false,
  error: null
};

const waitTimeSlice = createSlice({
  name: 'waitTime',
  initialState,
  reducers: {
    setWaitTimes: (state, action) => {
      state.waitTimes = action.payload.waitTimes;
      state.nextUnavailable = action.payload.nextUnavailable;
      state.metadata = action.payload.metadata;
      state.lastCalculationTime = new Date().toISOString();
      state.error = null;
    },
    setManagerStatus: (state, action) => {
      state.isManagerActive = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetWaitTimes: (state) => {
      state.waitTimes = initialState.waitTimes;
      state.nextUnavailable = null;
      state.metadata = null;
      state.lastCalculationTime = null;
      state.error = null;
    }
  }
});

export const {
  setWaitTimes,
  setManagerStatus,
  setLoading,
  setError,
  clearError,
  resetWaitTimes
} = waitTimeSlice.actions;

// Ein Listener je Fenster; mehrfaches Abonnieren waere verschwendete Quote.
let waitTimeUnsubscribe = null;

/**
 * Abonniert `waitTimes/current`. Liefert die Abmeldefunktion zurueck, damit
 * Aufrufer sich wie zuvor beim Manager verhalten koennen.
 */
export const subscribeToWaitTimes = () => (dispatch) => {
  if (waitTimeUnsubscribe) return waitTimeUnsubscribe;

  dispatch(setLoading(true));

  waitTimeUnsubscribe = onSnapshot(
    doc(db, 'waitTimes', 'current'),
    (snapshot) => {
      if (!snapshot.exists()) {
        // Noch nie berechnet: Anzeige auf dem Startwert lassen, aber nicht als
        // Fehler behandeln — die erste Cloud-Function-Berechnung fuellt das Dokument.
        dispatch(setLoading(false));
        dispatch(setManagerStatus(true));
        return;
      }

      const data = snapshot.data();
      dispatch(setWaitTimes({
        waitTimes: {
          general: data.general ?? initialState.waitTimes.general,
          grossraum: data.grossraum ?? initialState.waitTimes.grossraum,
          kleinbus7: data.kleinbus7 ?? initialState.waitTimes.kleinbus7,
          kleinbus8: data.kleinbus8 ?? initialState.waitTimes.kleinbus8
        },
        nextUnavailable: data.nextUnavailable ?? null,
        metadata: data.metadata ?? null
      }));
      dispatch(setLoading(false));
      dispatch(setManagerStatus(true));
    },
    (error) => {
      console.error('Wartezeiten konnten nicht geladen werden:', error);
      dispatch(setError(error.message));
      dispatch(setManagerStatus(false));
    }
  );

  return waitTimeUnsubscribe;
};

export const unsubscribeFromWaitTimes = () => (dispatch) => {
  if (waitTimeUnsubscribe) {
    waitTimeUnsubscribe();
    waitTimeUnsubscribe = null;
  }
  dispatch(setManagerStatus(false));
  dispatch(resetWaitTimes());
};

export const selectWaitTimes = (state) => state.waitTime.waitTimes;
export const selectNextUnavailable = (state) => state.waitTime.nextUnavailable;
export const selectWaitTimeMetadata = (state) => state.waitTime.metadata;
export const selectIsManagerActive = (state) => state.waitTime.isManagerActive;
export const selectWaitTimeLoading = (state) => state.waitTime.isLoading;
export const selectWaitTimeError = (state) => state.waitTime.error;
export const selectLastCalculationTime = (state) => state.waitTime.lastCalculationTime;

export default waitTimeSlice.reducer;
